import React from 'react';
import { useNavigate } from 'react-router-dom';
import { generateLobbySlug } from '../utils/lobbyUtils';
import { SITE_GAMES, inDevelopment } from './site/games';
import type { GameDef } from '../games/registry';

// The games list, built from the registry (src/games/registry.ts): no rules or features are kept here.
// Each game's rules page (/games/:id) shows its own rules file or its built-in rules text.

// Card art in public/assets; a game without one gets a letter tile.
const ICONS: Record<string, string> = {
  'classic-cards': '/assets/icon-war.jpg',
  'dice-factory': '/assets/icon-dice-factory.jpg',
  'heist-city': '/assets/icon-heist-city.JPG',
};

const players = (g: GameDef) => (g.minPlayers === g.maxPlayers ? `${g.minPlayers} players` : `${g.minPlayers}–${g.maxPlayers} players`);

function Icon({ game }: { game: GameDef }) {
  const src = ICONS[game.id];
  if (src) return <img src={src} alt="" className="w-16 h-16 rounded-lg object-cover border-2 border-lion flex-none" />;
  return (
    <div aria-hidden="true" className="w-16 h-16 rounded-lg border-2 border-lion flex-none flex items-center justify-center bg-payne-grey-dark text-2xl font-bold text-lion-light">
      {game.name[0]}
    </div>
  );
}

const ExplorePage: React.FC = () => {
  const navigate = useNavigate();
  // Finished games first; those still being designed in a section of their own.
  const ready = SITE_GAMES.filter(g => !inDevelopment(g));
  const dev = SITE_GAMES.filter(inDevelopment);
  const createRoom = (gameId: string) => navigate(`/room/${generateLobbySlug()}?game=${gameId}`);

  return (
    <div className="min-h-screen bg-payne-grey-dark text-white">
      <header className="p-6 border-b border-payne-grey">
        <div className="flex flex-wrap items-center justify-between gap-4 max-w-5xl mx-auto">
          <div>
            <h1 className="text-4xl font-bold mb-1 text-lion-light">Explore Games</h1>
            <p className="text-gray-300">Read how each game plays, or start a room with it.</p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="bg-payne-grey hover:bg-payne-grey-light px-5 py-2 rounded-lg transition-colors border border-payne-grey-light"
          >
            ← Home
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 flex flex-col gap-10">
        {([[ready, 'Ready to play', ''], [dev, 'In development', 'Still being designed: rough edges, and some may not play all the way through yet.']] as const).map(([list, title, note]) => list.length > 0 && (
        <section key={title} className="flex flex-col gap-4">
          <div>
            <h2 className="text-2xl font-semibold">{title}</h2>
            {note && <p className="text-gray-400 text-sm">{note}</p>}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
        {list.map(game => (
          <article key={game.id} className="bg-payne-grey p-5 rounded-xl border border-payne-grey-light flex flex-col gap-4">
            <div className="flex gap-4">
              <Icon game={game} />
              <div className="min-w-0">
                <h2 className="text-xl font-semibold text-lion-light">{game.name}</h2>
                <p className="text-sm text-gray-300">
                  {players(game)}
                  {game.status !== 'playable' && <span className="ml-2 px-2 py-0.5 rounded bg-amber-400/20 text-amber-300 text-xs">In development</span>}
                </p>
              </div>
            </div>
            <p className="text-gray-200 flex-1">{game.description}</p>
            {game.rulesets.length > 1 && (
              <p className="text-sm text-gray-400">Options: {game.rulesets.map(r => r.name).join(', ')}</p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => navigate(`/games/${game.id}`)}
                className="flex-1 bg-payne-grey-light hover:bg-payne-grey-dark py-2.5 px-4 rounded-lg transition-colors border border-payne-grey-light font-semibold"
              >
                Read the rules
              </button>
              <button
                onClick={() => createRoom(game.id)}
                className="flex-1 bg-lion hover:bg-lion-light text-payne-grey-dark py-2.5 px-4 rounded-lg transition-colors font-semibold"
              >
                Create a room
              </button>
            </div>
          </article>
        ))}
          </div>
        </section>
        ))}
      </main>
    </div>
  );
};

export default ExplorePage;
